import { Injectable } from '@angular/core';
import camelcaseKeys from 'camelcase-keys';
import { PaperworkState } from '@store/reducers';
import { HttpClient } from '@angular/common/http';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { environment } from '@environments/environment';
import { catchError, map, retry } from 'rxjs/operators';
import { PdfService } from '@shared/helpers/pdf.helper';
import { AuthService } from '@auth/services/auth.service';
import { API_URIS } from '@shared/constants/api-uris.const';
import { Vaccine } from '@shared/interfaces/vaccines.interface';
import { StatusService } from '@shared/services/status.service';
import { Paperwork } from '@shared/interfaces/paperwork.interface';
import { PCV2Request } from '@shared/interfaces/request.interface';
import { FileExtension } from '@shared/constants/mime-types.const';
import { Procedure } from '@shared/interfaces/dermatology.interface';
import { buildContentPdfExams } from '@clinical/indications/utils/build-pdf-exams';
import { ExamIndication } from '@clinical/indications/interfaces/indication.interface';
import { buildContentPdfVaccines } from '@clinical/indications/utils/build-pdf-vaccines';
import { buildContentPdfDermatologia } from '@clinical/indications/utils/build-pdf-dermatology';
import { buildContentPdfEyeExam } from '@clinical/indications/utils/build-pdf-ophthalmology-exam';
import { buildContentPdfOtorrinolaringologist } from '@clinical/indications/utils/build-pdf-otorhinolaryngologist';

const BASE_API: string = environment.baseApiRME;
const BASE_API_PCV2: string = environment.baseApiPCv2;
const URI_PROXY_PATIENTUCC: string = API_URIS.proxy.patientUcc;
const URI_CLINICAL_RECIPES: string = API_URIS.clinical.indications;
const URI_KEIRON_NOTIFICATION: string = API_URIS.keiron.notification;

@Injectable({
    providedIn: 'root',
})
export class ClinicalIndicationService {
    constructor(
        private httpClient: HttpClient,
        private pdfService: PdfService,
        private authService: AuthService,
        private statusService: StatusService
    ) { }

    findVaccine(): Observable<Vaccine[]> {
        return this.httpClient
            .get<PCV2Request>(`${BASE_API_PCV2}/mantenedores/v1/vacunas`)
            .pipe(map(({ data }: { data: Vaccine[] }) => data));
    };

    getDermatology(text: string): Observable<{ status: string; request: any }> {
        if (text === ' ') return of({ status: 'noSearch', request: [] });
        return this.httpClient.get<Procedure[]>(`${BASE_API}/v1/indicaciones/dermatologia?text=${text}`).pipe(
            retry(3),
            map(this.requestProcedure),
            catchError(this.statusService.catchError));
    };

    requestProcedure(proc: Procedure[]): { status; request } {
        if (proc.length === 0) return { status: 'void', request: [] };
        if (proc.length > 0) return { status: 'ok', request: proc };
    };

    findExamsByTypeAndName(type: string, searchText: string): Observable<{ status: string; request: any }> {
        const ENDPOINT = `v1/indicaciones/${type}?text=${searchText}`;
        const url = `${BASE_API}/${ENDPOINT}`;
        return this.httpClient.get<ExamIndication[]>(url).pipe(
            retry(3),
            map(response => camelcaseKeys(response)),
            map(this.convertExamsToPCv2Exam),
            catchError(this.statusService.catchError));
    };

    getExamByGroupAndId(group: string, id: string): Observable<ExamIndication> {
        const type = this.convertGroupToType(group);
        const ENDPOINT = `v1/indicaciones/${type}?id=${id}`;
        const url = `${BASE_API}/${ENDPOINT}`;
        return this.httpClient.get<ExamIndication[]>(url)
            .pipe(
                retry(3),
                map((exams) => {
                    const result = this.convertExamsToPCv2Exam(exams);
                    return result?.status === 'ok' ? result.request[0] : null;
                }),
                catchError(this.statusService.catchError),
            );
    };

    getUpdatedExams(indications: ExamIndication[]): Observable<ExamIndication[]> {
        const updatedIndicationsRequests = indications.map(({ group, id }) => this.getExamByGroupAndId(group, id));
        return combineLatest(updatedIndicationsRequests).pipe(
            map((updatedIndications) =>
                indications
                    .map((exam, i) => updatedIndications[i] ? {
                        ...exam, ...updatedIndications[i],
                        observations: exam.observations,
                    } : null)
                    .filter(exam => exam !== null),
            ),
            catchError(() => of(indications)),
        );
    };

    putIndication(body: Paperwork<ExamIndication>): Observable<Paperwork<ExamIndication>> {
        return this.httpClient.post<PCV2Request>(`${BASE_API_PCV2}/${URI_CLINICAL_RECIPES}`, body).pipe(
            retry(3), map(({ data }: { data: Paperwork<ExamIndication> }) => data),
        );
    };

    addMarksByPatient(patientId: string, marksId: string[]): Observable<any> {
        const apiPatientStageByEnviroment = { dev: '-test', qa: '-pre', prd: '-prod' };
        const suffixUrlByStage = `pacientes${apiPatientStageByEnviroment[environment.stage]}/Marcas`;
        const endpoint = `${BASE_API_PCV2}/${URI_PROXY_PATIENTUCC}${suffixUrlByStage}&domain=actualizarMarcasPaciente&client=planesSalud`;
        const body = { idPaciente: patientId, idMarca: marksId };
        return this.httpClient.post<PCV2Request>(endpoint, body);
    };

    convertExamsToPCv2Exam(exam): { status: string; request: any[] } {
        if (exam.length === 0) return { status: 'void', request: [] };
        const exams = exam.map(ex => {
            const { id, csu, codfon, glosa, grupo, obs, prmId, booking } = ex;
            return {
                id,
                glosa,
                group: grupo,
                sapId: csu,
                imedId: ex?.imed || ex?.imedId,
                fonasaId: codfon ? codfon.toString() : undefined,
                observations: obs,
                prmId: prmId ? prmId.toUpperCase() : undefined,
                booking: booking ? booking.toLowerCase() : undefined,
            };
        });
        return { status: 'ok', request: exams };
    };

    convertGroupToType(group: string) {
        if (group.toUpperCase() === 'LABORATORIO') return 'laboratorios';
        return group.toLowerCase();
    };

    /**
     * Notifica a Keiron enviando los datos del formulario mediante una solicitud HTTP POST.
     *
     * @param formData - Los datos del formulario a enviar en la notificación.
     * @returns Un observable que emite la respuesta de la API, específicamente el campo `data`.
     *          En caso de error, retorna un objeto vacío.
     */
    notifyKeiron(formData: FormData) {
        const endpoint = `${BASE_API_PCV2}/${URI_KEIRON_NOTIFICATION}`;
        return this.httpClient.post<PCV2Request>(`${endpoint}`, formData).pipe(
            retry(3),
            map(({ data }) => (data)),
            catchError((error) => of({})),
        );
    };

    /**
     * Notifica a Keiron para cada uno de los elementos en el arreglo de formularios proporcionado.
     * 
     * @param formsData - Un arreglo de objetos FormData que contienen la información de los formularios a notificar.
     * @returns Un observable que emite un arreglo con las respuestas de cada notificación a Keiron, en el mismo orden que los formularios originales.
     */
    notifyAllKeiron(formsData: FormData[]) {
        const request = formsData.map(formData => this.notifyKeiron(formData));
        return forkJoin(request);
    };

    /**
     * Construye un arreglo de objetos FormData para notificar formularios de Keiron.
     * 
     * @param paperworkState - Estado actual del trámite que contiene información del paciente, profesional, cita y otros datos relevantes.
     * @returns Una promesa que resuelve en un arreglo de objetos FormData, cada uno representando un formulario a notificar con los datos y archivos correspondientes.
     * 
     * @remarks
     * - Extrae información relevante del paciente, profesional y cita desde el estado del trámite.
     * - Obtiene los archivos PDF asociados mediante `getKeironPDFs`.
     * - Por cada archivo, crea un objeto FormData con los datos requeridos para la notificación.
     */
    async buildNotifyKeironFormsData(paperworkState): Promise<FormData[]> {
        const { patient, professional, shortId, suffix, appointment } = paperworkState.paperwork;
        const files = await this.getKeironPDFs(paperworkState);

        console.log("files ==>", files);

        return files.map(file => {
            const notifyFormData = new FormData();
            notifyFormData.append('file', file.file);
            notifyFormData.append('suffix', suffix);
            notifyFormData.append('professionalRut', professional.rut);
            notifyFormData.append('professionalName', `${professional.names || ''} ${professional.surnames || ''}`);
            notifyFormData.append('professionalSpecialty', professional.specialty?.name || '');
            notifyFormData.append('professionalCenter', appointment?.center || '');
            notifyFormData.append('orderName', this.getOrderName(paperworkState.paperwork, file));
            notifyFormData.append('orderNumber', shortId);
            notifyFormData.append('patientRut', patient.documentNumber || '');
            notifyFormData.append('patientName', `${patient.names || ''} ${patient.surnames || ''}`);
            notifyFormData.append('patientBirthDate', patient.birth || '');
            notifyFormData.append('patientEmail', patient.email || '');
            notifyFormData.append('patientPhone', patient.phone || '');

            return notifyFormData;
        });
    };

    /**
     * Genera y retorna el primer documento PDF de Keiron basado en el estado del trámite proporcionado.
     *
     * Este método agrupa las filas del trámite, genera un PDF para cada grupo utilizando una plantilla específica,
     * y retorna el primer documento generado en formato de objeto con información adicional como título, extensión,
     * ruta y archivo Blob.
     *
     * @param paperworkState Estado actual del trámite, que incluye la definición y los datos del trámite.
     * @returns Una promesa que resuelve con el primer documento PDF generado, incluyendo su base64, título, extensión,
     *          ruta y archivo Blob.
     */
    async getKeironPDFs(paperworkState: PaperworkState) {
        let docBase64 = null;
        const { definition } = paperworkState;
        const groups = this.groupedRows(paperworkState);
        const docs = { pdfs: [], paperwork: {} as Paperwork };
        const { paperwork } = paperworkState;
        const professional = this.authService.professionalRef;

        for await (const group of groups) {
            const { title } = group;
            const { config, docTitle } = definition.submodule;
            const body = this.getTemplatePdfIndication(paperwork, group, paperwork.suffix);
            const pdfParams = {
                config: { ...config, isPreview: false },
                paperwork: { ...paperwork, professional },
                group, title, body, docTitle,
            };
            const base64 = await this.pdfService.createPdf(pdfParams);
            const pdf = { base64, title: title + ' Keiron', isAttached: true };

            docBase64 = base64;
            docs.pdfs = [pdf, ...docs.pdfs];
        }

        const extension: FileExtension = 'pdf';
        const documents = docs.pdfs.map((pdf) => ({
            ...pdf,
            extension,
            path: definition.module,
            file: this.pdfService.base64ToBlob(pdf.base64),
        }));
        return documents;
    };

    /**
     * Agrupa los formularios contenidos en el objeto `paperwork` según el valor de su propiedad `group`.
     *
     * @param paperwork - Objeto que contiene la propiedad `content`, un arreglo de formularios que pueden tener la propiedad `group`.
     * @returns Un arreglo de objetos, cada uno representando un grupo. Cada objeto contiene:
     *   - `title`: El nombre del grupo en minúsculas (o una cadena vacía si no existe).
     *   - `rows`: Un arreglo con los formularios que pertenecen a ese grupo.
     */
    private groupedRows({ paperwork }): any[] {
        const groups: string[] = [...new Set<string>(paperwork.content.map((form) => form?.group))];
        return groups.map<any>((group) => ({
            title: group ? group.toLowerCase() : '',
            rows: paperwork.content.filter((form) => form.group === group),
        }));
    };

    /**
     * Genera el contenido PDF correspondiente para una indicación clínica según el sufijo proporcionado.
     *
     * @param paperwork - Objeto que contiene la información del trámite clínico.
     * @param group - Grupo de datos relevante para la generación del PDF.
     * @param suffix - Sufijo que indica el tipo de indicación clínica (por ejemplo: 'DRM', 'OFT', 'EXA', etc.).
     * @returns El contenido PDF generado para el tipo de indicación especificado.
     */
    private getTemplatePdfIndication(paperwork: Paperwork, group, suffix: string) {
        switch (suffix) {
            case 'DRM':
                return buildContentPdfDermatologia(paperwork, group);
            case 'EXA':
                return buildContentPdfExams(paperwork, group);
            case 'EOF':
                return buildContentPdfEyeExam(paperwork, group);
            case 'VAC':
                return buildContentPdfVaccines(paperwork, group);
            case 'OTR':
                return buildContentPdfOtorrinolaringologist(paperwork, group);
        }
    };

    /**
     * Obtiene el nombre de la orden basado en el sufijo del objeto `paperwork` y, opcionalmente, en el título del archivo proporcionado.
     *
     * @param paperwork Objeto que contiene información sobre el trámite, incluyendo el sufijo que determina el tipo de orden.
     * @param file (Opcional) Archivo relacionado, utilizado para determinar el nombre de la orden en ciertos casos según su título.
     * @returns El nombre de la orden correspondiente según el sufijo y, en algunos casos, el título del archivo.
     */
    private getOrderName(paperwork: Paperwork, file?: any): string {
        switch (paperwork.suffix) {
            case 'DRM':
                return 'Procedimientos Dermatología';
            case 'EXA':
                if (file?.title?.toLowerCase().includes('procedimientos'))
                    return 'Procedimientos';
                else if (file?.title?.toLowerCase().includes('laboratorio'))
                    return 'Laboratorio';
                else
                    return 'Imágenes';
            case 'EOF':
                return 'Exámenes Oftalmología';
            case 'VAC':
                return 'Vacunas';
            case 'OTR':
                return 'Exámenes y Comité Otorrinolaringología';
        }
    };
}
